// Telas de Contas a Pagar e a Receber (issue #16).
//
// Uma tela só, parametrizada pela direção: as duas Contas são o mesmo agregado
// (ADR-0011), então duplicar a tela seria repetir a mesma listagem, os mesmos
// filtros e o mesmo formulário de Pagamento com outro rótulo. O que muda é a
// Fonte — que só a Conta a Receber tem — e os textos.

import { newId } from "../../domain/ids.ts";
import { formatData, hojeISO } from "../../domain/datas.ts";
import { formatReais } from "../../domain/money.ts";
import {
  contaVencida,
  pagamentosDaConta,
  situacaoPagamento,
  subtotalItem,
  totalContaPagar,
  totalPago,
  valorEmAberto,
  type Conta,
  type DadosConta,
  type DadosContaPagar,
  type DadosContaReceber,
  type DirecaoConta,
  type FonteConta,
  type ItemConta,
  type SituacaoPagamento,
} from "../../domain/contas.ts";
import { rotuloCategoriaFinanceira } from "../../domain/categorias-financeiras.ts";
import type { Insumo } from "../../domain/snapshot.ts";
import type { Store } from "../store.ts";
import { clear, el, linhaTotalTabela, openModal, tabelaPaginada, botao } from "../ui.ts";
import {
  abrirConfirmacao,
  abrirFormModal,
  barraCadastro,
  campo,
  campoObrigatorio,
  campoInline,
  inputData,
  inputTexto,
  select,
  telaLista,
} from "./_cadastro.ts";
import { criarAcaoComando } from "./_acao.ts";
import { inputMoeda, type CampoMoeda } from "./_input-moeda.ts";
import { categoriaFinanceiraSelect } from "./_categoria-financeira-select.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo } from "./_lista-filtros.ts";
import { podeEditarModulo } from "./_permissao.ts";

const ROTULO_SIT: Record<SituacaoPagamento, string> = {
  pendente: "Pendente",
  parcial: "Parcial",
  quitada: "Quitada",
};

const ROTULO_FONTE: Record<FonteConta, string> = {
  cliente: "Cliente",
  emprestimo: "Empréstimo",
  transferencia: "Transferência",
  outro: "Outro",
};

/** As Fontes que o operador escolhe à mão. "cliente" vem da Fatura (#41). */
const FONTES_MANUAIS: FonteConta[] = ["emprestimo", "transferencia", "outro"];

interface Textos {
  titulo: string;
  novo: string;
  colValor: string;
  /** Substantivo, para as frases ("Pagamento removido.", "Valor do pagamento"). */
  verbo: string;
  /** Verbo curto, para o botão da linha — cabe ao lado de Editar/Excluir. */
  acaoLinha: string;
  vencidasLabel: string;
}

const TEXTOS: Record<DirecaoConta, Textos> = {
  pagar: {
    titulo: "Conta a Pagar",
    novo: "Nova Conta a Pagar",
    colValor: "Valor",
    verbo: "Pagamento",
    acaoLinha: "Pagar",
    vencidasLabel: "Vencidas em Aberto",
  },
  receber: {
    titulo: "Conta a Receber",
    novo: "Nova Conta a Receber",
    colValor: "Valor",
    verbo: "Recebimento",
    acaoLinha: "Receber",
    vencidasLabel: "Vencidas em Aberto",
  },
};

export function renderContasPagar(c: HTMLElement, s: Store): () => void {
  return renderContas(c, s, "pagar");
}

export function renderContasReceber(c: HTMLElement, s: Store): () => void {
  return renderContas(c, s, "receber");
}

function renderContas(
  container: HTMLElement,
  store: Store,
  direcao: DirecaoConta,
): () => void {
  const t = TEXTOS[direcao];
  // filtros, busca e página num estado só (#93): null = todos
  const filtros = new EstadoLista({
    categoria: null as string[] | null,
    situacao: null as string[] | null,
    vencidas: false,
  });
  const busca = buscaDaLista(filtros, "Buscar por descrição…", () => render());
  const fCategoria = filtroMultiplo(filtros, "categoria", "Categoria", () => render());
  const fSituacao = filtroMultiplo(filtros, "situacao", "Situação", () => render());

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "financeiro");
    const hoje = hojeISO();
    const catNome = new Map(
      snap.categoriasFinanceiras.map((c) => [c.id, rotuloCategoriaFinanceira(c)]),
    );
    const cliNome = new Map(snap.clientes.map((c) => [c.id, c.nome]));

    const categoriasUsadas = [
      ...new Set(snap.contas.filter((c) => c.direcao === direcao).map((c) => c.categoriaId)),
    ]
      .map((id) => [id, catNome.get(id) ?? id] as const)
      .sort((a, b) => a[1].localeCompare(b[1], "pt-BR"));

    const catItens = categoriasUsadas.map((c) => ({ id: c[0], nome: c[1] }));
    fCategoria.atualizar(catItens);
    fSituacao.atualizar([
      { id: "pendente", nome: "Pendente" },
      { id: "parcial", nome: "Parcial" },
      { id: "quitada", nome: "Quitada" },
    ]);
    const catsFiltro = fCategoria.selecionados();
    const sitFiltro = fSituacao.selecionados();
    const fVencidas = filtros.filtro("vencidas");
    const lista = snap.contas
      .filter((c) => c.direcao === direcao)
      .filter((c) => catsFiltro.size === 0 || catsFiltro.has(c.categoriaId))
      .filter((c) => sitFiltro.size === 0 || sitFiltro.has(situacaoPagamento(snap, c)))
      .filter((c) => !fVencidas || contaVencida(snap, c, hoje))
      .filter((c) =>
        busca.casa(
          c.descricao,
          catNome.get(c.categoriaId),
          c.fornecedor,
          c.observacao,
        ),
      )
      // vencimento mais próximo primeiro: é a ordem em que se paga
      .sort((a, b) => a.vencimento.localeCompare(b.vencimento));

    const vencidas = snap.contas.filter(
      (c) => c.direcao === direcao && contaVencida(snap, c, hoje),
    );
    const alternarVencidas = botao(
      fVencidas ? "primario" : "secundario",
      {},
      `${t.vencidasLabel}: ${vencidas.length}`,
    );
    alternarVencidas.addEventListener("click", () => {
      filtros.mudarFiltro("vencidas", !fVencidas);
      render();
    });

    const barra = barraCadastro(
      [fCategoria.element, fSituacao.element],
      t.novo,
      () => abrirModal(null),
      () => {
        filtros.limpar();
        render();
      },
      [alternarVencidas],
      podeGravar,
    );

    if (lista.length === 0) {
      container.append(
        telaLista(
          barra,
          busca.elemento(),
          el(
            "p",
            { class: "muted lista-vazia" },
            snap.contas.some((c) => c.direcao === direcao)
              ? filtros.mensagemVazio({ nenhum: "Nenhuma conta.", filtrado: "Nenhuma conta com esses filtros." })
              : `Nenhuma ${t.titulo.toLowerCase()}.`,
          ),
        ),
      );
      return;
    }

    // Acumulado junto com o cálculo por linha (abaixo), não numa segunda
    // passagem: `valorEmAberto` escaneia `snap.pagamentos` inteiro por
    // Conta, então repetir isso só pro total custaria caro numa lista grande.
    let totalValor = 0;
    let totalAberto = 0;
    const linhas = lista.map((c) => {
      const sit = situacaoPagamento(snap, c);
      const aberto = valorEmAberto(snap, c);
      const venceu = contaVencida(snap, c, hoje);
      totalValor += c.valorCentavos;
      totalAberto += aberto;

      const acoesConta: Node[] = [];
      if (podeGravar) {
        const baixar = botao("secundario", {}, t.acaoLinha);
        baixar.addEventListener("click", () => abrirPagamentos(store, c.id, direcao));
        const editar = botao("secundario", {}, "Editar");
        editar.addEventListener("click", () => abrirModal(c));
        const excluir = botao("perigo", {}, "Excluir");
        excluir.addEventListener("click", () => {
          abrirConfirmacao({
            titulo: "Excluir esta Conta?",
            mensagem: `"${c.descricao}" e os ${t.verbo.toLowerCase()}s dela saem do sistema. Não dá para desfazer.`,
            rotuloConfirmar: "Excluir",
            perigo: true,
            aoConfirmar: () =>
              void store.executar(
                { agregado: "conta", cmd: { tipo: "excluir", id: c.id } },
                "Conta excluída.",
              ),
          });
        });
        acoesConta.push(baixar, editar, excluir);
      }

      return el(
        "tr",
        { class: sit === "quitada" ? "inativo" : "" },
        el("td", {}, c.descricao),
        el("td", { class: "muted" }, catNome.get(c.categoriaId) ?? "—"),
        direcao === "pagar"
          ? el("td", { class: "muted" }, c.fornecedor || "—")
          : el(
              "td",
              { class: "muted" },
              c.fonte === "cliente"
                ? (cliNome.get(c.clienteId ?? "") ?? "Cliente")
                : (ROTULO_FONTE[c.fonte ?? "outro"] ?? "—"),
            ),
        el(
          "td",
          {
            class: venceu ? "prazo-vencido" : "muted",
            "data-sort": c.vencimento,
          },
          formatData(c.vencimento),
        ),
        el(
          "td",
          { class: "num", "data-sort": String(c.valorCentavos) },
          formatReais(c.valorCentavos),
        ),
        el(
          "td",
          { class: "num", "data-sort": String(aberto) },
          aberto === 0 ? "—" : formatReais(aberto),
        ),
        el("td", {}, el("span", { class: `pill-fat pill-fat-${sit}` }, ROTULO_SIT[sit])),
        el("td", { class: "acoes" }, ...acoesConta),
      );
    });

    // Larguras apertadas de propósito: Contas a Receber tem 7 colunas + as
    // ações. Os valores em R$ ganham espaço para não cortar; a Descrição é a
    // que cede (e a coluna de ações fica fixa à direita, então rolar na
    // horizontal não esconde Editar/Excluir).
    const colunas = [
      { t: "Descrição", w: 150 },
      { t: "Categoria", w: 104 },
      ...(direcao === "pagar" ? [{ t: "Fornecedor", w: 110 }] : [{ t: "Fonte", w: 96 }]),
      { t: "Vencimento", w: 110 },
      { t: t.colValor, w: 116, ord: true as const },
      { t: "Em aberto", w: 116, ord: true as const },
      { t: "Situação", w: 112 },
      { t: "", ord: false as const },
    ];
    // Deriva do próprio `colunas` em vez de contar na mão — a linha de total
    // não pode ficar desalinhada se uma coluna for adicionada/removida ali.
    const idxValor = colunas.findIndex((c) => c.t === t.colValor);

    // Linha de total, parte da própria tabela (tfoot) — soma o filtro
    // inteiro (`lista`, já acumulado acima), não só a página que
    // `tabelaPaginada` mostra.
    const linhaTotal = linhaTotalTabela(
      colunas.length,
      idxValor,
      `Total · ${lista.length} conta(s)`,
      [
        { rotulo: t.colValor, texto: formatReais(totalValor) },
        { rotulo: "Em aberto", texto: formatReais(totalAberto) },
      ],
    );

    container.append(
      telaLista(
        barra,
        busca.elemento(),
        tabelaPaginada(
          direcao === "pagar" ? "contas-pagar" : "contas-receber",
          colunas,
          linhas,
          filtros.tabela,
          render,
          { linhaTotal },
        ),
      ),
    );
  };

  const abrirModal = (atual: Conta | null): void => {
    const snap = store.get();
    const descricao = inputTexto("Descrição", atual?.descricao ?? "");
    const vencimento = inputData(atual?.vencimento ?? hojeISO());
    const categoria = categoriaFinanceiraSelect(store, direcao, atual?.categoriaId ?? "");

    // Campos específicos de Conta a Pagar: Fornecedor, Observação e Itens
    const fornecedor = inputTexto("Fornecedor (opcional)", atual?.fornecedor ?? "");
    const observacao = el("textarea", {
      rows: "2",
      placeholder: "Observações adicionais (opcional)",
      class: "input-obs",
    }) as HTMLTextAreaElement;
    observacao.value = atual?.observacao ?? "";

    const itensIniciais = atual?.itens ?? [];
    // o avulso vem gravado (#87) — nunca reconstruído por subtração
    const centavosManualInicial = atual
      ? atual.direcao === "pagar"
        ? atual.avulsoCentavos
        : atual.valorCentavos
      : null;

    const valorManual = inputMoeda({
      centavos: centavosManualInicial,
      ariaLabel: direcao === "pagar" ? "Valor avulso" : "Valor da conta",
    });

    interface LinhaItem {
      insumoSelect: HTMLSelectElement;
      unidadeInput: HTMLInputElement;
      qtdInput: HTMLInputElement;
      valorUnitMoeda: CampoMoeda;
      subtotalSpan: HTMLElement;
      element: HTMLElement;
      subtotalCentavos: () => number;
      dados: () => ItemConta | null;
    }

    let linhasItens: LinhaItem[] = [];
    const containerLinhas = el("div", { class: "itens-conta-linhas" });
    const resumoItens = el("div", { class: "itens-conta-resumo" });

    const recalcularTotal = (): void => {
      for (const l of linhasItens) l.subtotalCentavos(); // atualiza o subtotal de cada linha
      // a mesma conta que o domínio grava, sobre os mesmos itens que vão ser salvos
      const itens = linhasItens.map((l) => l.dados()).filter((d): d is ItemConta => d != null);
      const avulso = valorManual.centavos();
      const somaItens = totalContaPagar(itens, null);
      const manual = avulso ?? 0;
      const total = totalContaPagar(itens, avulso);

      if (linhasItens.length > 0) {
        resumoItens.hidden = false;
        clear(resumoItens);
        resumoItens.append(
          el("span", {}, `Itens: ${formatReais(somaItens)} + Avulso: ${formatReais(manual)}`),
          el("span", {}, el("b", {}, `Total: ${formatReais(total)}`)),
        );
      } else {
        resumoItens.hidden = true;
      }
    };

    valorManual.input.addEventListener("input", recalcularTotal);

    const insumosDisponiveis = snap.insumos
      .filter((i) => i.ativo || itensIniciais.some((it) => it.insumoId === i.id))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    // Nomes repetidos (marcas diferentes, issue #67) ganham a marca no rótulo
    // pra não ficarem indistinguíveis no combobox.
    const contagemPorNome = new Map<string, number>();
    for (const i of insumosDisponiveis) {
      const nome = i.nome.toLowerCase();
      contagemPorNome.set(nome, (contagemPorNome.get(nome) ?? 0) + 1);
    }
    const nomesRepetidos = new Set(
      [...contagemPorNome].filter(([, n]) => n > 1).map(([nome]) => nome),
    );
    const rotuloInsumo = (ins: Insumo): string =>
      nomesRepetidos.has(ins.nome.toLowerCase()) && ins.marca
        ? `${ins.nome} (${ins.marca})`
        : ins.nome;

    const criarLinhaItem = (it?: ItemConta): LinhaItem => {
      const selectInsumo = el("select", { "aria-label": "Produto / Insumo" }) as HTMLSelectElement;
      selectInsumo.append(el("option", { value: "", "data-vazio": "" }, "Selecione o produto/insumo..."));
      for (const ins of insumosDisponiveis) {
        const opt = el("option", { value: ins.id }, rotuloInsumo(ins)) as HTMLOptionElement;
        if (it?.insumoId === ins.id) opt.selected = true;
        selectInsumo.append(opt);
      }

      const inputUnidade = el("input", {
        type: "text",
        class: "item-unidade",
        placeholder: "Un.",
        value: it?.unidade ?? "",
        "aria-label": "Unidade de medida",
      }) as HTMLInputElement;

      selectInsumo.addEventListener("change", () => {
        const ins = insumosDisponiveis.find((i) => i.id === selectInsumo.value);
        if (ins && !inputUnidade.value) {
          inputUnidade.value = ins.unidade;
        }
        recalcularTotal();
      });

      const inputQtd = el("input", {
        type: "number",
        min: "0",
        step: "any",
        class: "item-qtd",
        placeholder: "Qtd",
        value: it && it.quantidade ? String(it.quantidade) : "",
        "aria-label": "Quantidade",
      }) as HTMLInputElement;

      const valorUnit = inputMoeda({
        centavos: it?.valorUnitarioCentavos ?? null,
        ariaLabel: "Valor unitário",
      });

      const spanSubtotal = el("span", { class: "item-subtotal" }, "R$ 0,00");
      const btnRemover = botao(
        { variante: "perigo", tamanho: "compacto" },
        { title: "Remover Item", "aria-label": "Remover Item" },
        "✕",
      );

      const linhaElement = el(
        "div",
        { class: "item-conta-linha" },
        selectInsumo,
        inputUnidade,
        inputQtd,
        valorUnit.element,
        spanSubtotal,
        btnRemover,
      );

      const subtotalCentavos = (): number => {
        const sub = subtotalItem({
          quantidade: parseFloat(inputQtd.value) || 0,
          valorUnitarioCentavos: valorUnit.centavos() || 0,
        });
        spanSubtotal.textContent = formatReais(sub);
        return sub;
      };

      inputQtd.addEventListener("input", () => {
        subtotalCentavos();
        recalcularTotal();
      });
      valorUnit.input.addEventListener("input", () => {
        subtotalCentavos();
        recalcularTotal();
      });

      const itemObj: LinhaItem = {
        insumoSelect: selectInsumo,
        unidadeInput: inputUnidade,
        qtdInput: inputQtd,
        valorUnitMoeda: valorUnit,
        subtotalSpan: spanSubtotal,
        element: linhaElement,
        subtotalCentavos,
        dados: () => {
          const insumoId = selectInsumo.value;
          const unidade = inputUnidade.value.trim();
          const quantidade = parseFloat(inputQtd.value) || 0;
          const valorUnitarioCentavos = valorUnit.centavos() || 0;
          if (!insumoId && !unidade && !quantidade && !valorUnitarioCentavos) {
            return null;
          }
          return {
            insumoId,
            unidade,
            quantidade,
            valorUnitarioCentavos,
          };
        },
      };

      btnRemover.addEventListener("click", () => {
        linhaElement.remove();
        linhasItens = linhasItens.filter((l) => l !== itemObj);
        recalcularTotal();
      });

      subtotalCentavos();
      return itemObj;
    };

    if (itensIniciais.length > 0) {
      for (const it of itensIniciais) {
        const l = criarLinhaItem(it);
        linhasItens.push(l);
        containerLinhas.append(l.element);
      }
    }

    const btnAdicionarItem = botao(
      { variante: "secundario", tamanho: "compacto" },
      {},
      "+ Adicionar Item de Produto / Insumo",
    );
    btnAdicionarItem.addEventListener("click", () => {
      const l = criarLinhaItem();
      linhasItens.push(l);
      containerLinhas.append(l.element);
      recalcularTotal();
    });

    const itensFieldset = el(
      "fieldset",
      { class: "itens-conta-editor" },
      el("legend", {}, "Itens da Conta (produtos / insumos)"),
      containerLinhas,
      btnAdicionarItem,
      resumoItens,
    );

    recalcularTotal();

    // Fonte só existe na Conta a Receber. A Fonte "cliente" é da Fatura (#41),
    // então não aparece aqui — mas se a Conta veio de lá, mostramos qual é.
    const daFatura = atual?.fonte === "cliente";
    const fonte = select(
      FONTES_MANUAIS.map((f) => [f, ROTULO_FONTE[f]] as const),
      atual?.fonte && !daFatura ? atual.fonte : "outro",
    );
    const clienteFixo = el(
      "p",
      { class: "muted" },
      `Cliente: ${cliNomeDe(snap, atual?.clienteId)} (veio de uma fatura)`,
    );

    abrirFormModal({
      titulo: atual ? `Editar ${atual.descricao}` : t.novo,
      largura: direcao === "pagar" ? "largo" : "padrao",
      rotuloSalvar: atual ? "Salvar" : "Criar",
      campos: () => {
        if (direcao === "pagar") {
          return el(
            "div",
            {},
            campoObrigatorio("Descrição", descricao),
            el(
              "div",
              { class: "grid-2" },
              campo("Fornecedor", fornecedor),
              campo("Categoria", categoria.element),
            ),
            itensFieldset,
            el(
              "div",
              { class: "grid-2" },
              campo("Valor avulso", valorManual.element),
              campo("Vencimento", vencimento),
            ),
            campo("Observação", observacao),
          );
        }
        return el(
          "div",
          {},
          campoObrigatorio("Descrição", descricao),
          campo("Categoria", categoria.element),
          campo("Fonte", daFatura ? clienteFixo : fonte),
          campo("Valor", valorManual.element),
          campo("Vencimento", vencimento),
        );
      },
      salvar: (aoErro) => {
        const itensValidos: ItemConta[] = [];
        for (const l of linhasItens) {
          const d = l.dados();
          if (d) itensValidos.push(d);
        }
        const manual = valorManual.centavos();
        // Conta a Pagar manda o avulso e os itens — o total é o domínio que
        // calcula (#87); a Receber, o valor (vazio = o domínio recusa)
        const valorDaConta: Pick<DadosContaPagar, "direcao" | "avulsoCentavos"> | Pick<DadosContaReceber, "direcao" | "valorCentavos"> =
          direcao === "pagar"
            ? { direcao, avulsoCentavos: manual }
            : { direcao, valorCentavos: manual ?? NaN };

        const dados: DadosConta = {
          ...valorDaConta,
          descricao: descricao.value,
          vencimento: vencimento.value,
          categoriaId: categoria.value(),
          fonte:
            direcao === "pagar"
              ? null
              : daFatura
                ? ("cliente" as const)
                : fonteDe(fonte.value),
          clienteId: daFatura ? (atual?.clienteId ?? null) : null,
          faturaId: atual?.faturaId ?? null,
          fornecedor: direcao === "pagar" ? fornecedor.value : null,
          observacao: direcao === "pagar" ? observacao.value : "",
          itens: direcao === "pagar" ? itensValidos : [],
        };
        const id = atual?.id ?? newId();
        const cmd = atual
          ? ({ tipo: "editar", id, dados } as const)
          : ({ tipo: "criar", id, dados } as const);
        return store.executar({ agregado: "conta", cmd }, undefined, aoErro);
      },
    });
  };

  render();
  return store.subscribe(render);
}

function cliNomeDe(snap: ReturnType<Store["get"]>, id: string | null | undefined): string {
  return snap.clientes.find((c) => c.id === id)?.nome ?? "—";
}

/** O `<select>` só oferece as três Fontes manuais — estreitar aqui é total. */
function fonteDe(v: string): FonteConta {
  return v === "emprestimo" ? "emprestimo" : v === "transferencia" ? "transferencia" : "outro";
}

// ------------------------------------------------------------- pagamentos ---

function abrirPagamentos(store: Store, contaId: string, direcao: DirecaoConta): void {
  const t = TEXTOS[direcao];
  openModal(`${t.verbo}s`, () => {
    const raiz = el("div", { class: "form-modal" });

    const render = (): void => {
      clear(raiz);
      const snap = store.get();
      const conta = snap.contas.find((c) => c.id === contaId);
      if (!conta) {
        raiz.append(el("p", { class: "muted" }, "Conta não encontrada."));
        return;
      }
      const pagos = pagamentosDaConta(snap, contaId);
      const aberto = valorEmAberto(snap, conta);

      const detalhes = [
        el("p", {}, el("b", {}, conta.descricao)),
      ];
      if (conta.fornecedor) {
        detalhes.push(el("p", { class: "muted" }, `Fornecedor: ${conta.fornecedor}`));
      }
      if (conta.observacao) {
        detalhes.push(el("p", { class: "muted" }, `Observação: ${conta.observacao}`));
      }
      detalhes.push(el(
        "p",
        { class: "muted" },
        `Valor ${formatReais(conta.valorCentavos)} · ` +
          `já ${direcao === "pagar" ? "pago" : "recebido"} ${formatReais(totalPago(snap, contaId))} · ` +
          `em aberto ${formatReais(aberto)}`,
      ));

      raiz.append(...detalhes);

      const lista = el("ul", { class: "lista-editavel" });
      if (pagos.length === 0) {
        lista.append(el("li", { class: "muted" }, `Nenhum ${t.verbo.toLowerCase()}.`));
      }
      for (const p of pagos) {
        const remover = botao({ variante: "secundario", tamanho: "compacto" }, {}, "Remover");
        remover.addEventListener("click", () => {
          void store
            .executar(
              { agregado: "conta", cmd: { tipo: "removerPagamento", id: p.id } },
              `${t.verbo} removido.`,
            )
            .then(render, render);
        });
        lista.append(
          el(
            "li",
            {},
            el("span", {}, `${formatData(p.data)} — ${formatReais(p.valorCentavos)}`),
            el("span", { class: "hist-acao" }, remover),
          ),
        );
      }

      const data = inputData(hojeISO());
      // atalho pro caso comum: pagou/recebeu no dia certo, sem digitar de novo
      // uma data que já está bem ali no vencimento da Conta.
      const usarVencimento = botao("secundario", {}, "Usar Vencimento");
      usarVencimento.addEventListener("click", () => {
        data.value = conta.vencimento;
      });
      // sugere o que falta: o caso comum é quitar de uma vez
      const valor = inputMoeda({ centavos: aberto || null, ariaLabel: `Valor do ${t.verbo}` });
      const erro = el("p", { class: "form-erro", role: "alert" });
      const incluir = botao("secundario", {}, `Registrar ${t.verbo}`);
      incluir.disabled = aberto === 0;
      incluir.addEventListener("click", () => {
        criarAcaoComando(store, erro)(
          {
            agregado: "conta",
            cmd: {
              tipo: "registrarPagamento",
              id: newId(),
              contaId,
              dados: { data: data.value, valorCentavos: valor.centavos() ?? NaN },
            },
          },
          `${t.verbo} registrado.`,
          render,
        );
      });

      raiz.append(
        lista,
        aberto === 0
          ? el("p", { class: "muted" }, "Conta quitada.")
          : el(
              "div",
              { class: "lista-add" },
              campoInline("Data", data),
              usarVencimento,
              campoInline(`Valor do ${t.verbo.toLowerCase()}`, valor.element),
              incluir,
            ),
        erro,
      );
    };

    render();
    return raiz;
  });
}
